import React from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'ghost'
  | 'stamp';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  const variantClasses: Record<ButtonVariant, string> = {
    primary:
      'bg-ink-900 text-paper-100 border border-ink-900 hover:bg-ink-800 shadow-paper active:shadow-pressed active:translate-y-0.5',
    secondary:
      'bg-paper-200 text-ink-900 border border-paper-400 hover:bg-paper-300 shadow-paper-sm active:translate-y-0.5',
    outline:
      'bg-transparent text-ink-800 border border-ink-700 hover:bg-paper-200 hover:text-ink-900 active:translate-y-0.5',
    danger:
      'bg-red-50 text-red-900 border-2 border-red-700 hover:bg-red-100 shadow-paper-sm active:translate-y-0.5',
    ghost:
      'bg-transparent text-ink-700 hover:text-ink-900 hover:bg-paper-200/80 active:bg-paper-300/80',
    stamp:
      'bg-paper-100 text-ink-900 border-2 border-ink-800 hover:bg-paper-200 shadow-paper rotate-[-0.5deg] hover:rotate-0 active:translate-y-0.5',
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'text-xs py-1 px-2.5 gap-1.5 font-medium',
    md: 'text-sm py-2 px-4 gap-2 font-semibold',
    lg: 'text-base py-2.5 px-6 gap-2.5 font-bold',
  };

  return (
    <button
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center font-serif tracking-wider uppercase
        rounded-xs transition-all duration-150 select-none
        disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>處理中...</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="inline-flex shrink-0">{leftIcon}</span>}
          <span>{children}</span>
          {rightIcon && <span className="inline-flex shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};
